import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { isWebView, isIOSWebView } from "./webviewDetector";
import { openPDFSimple } from "./simplePdfViewer";
import i18n from "../i18n";

const formatDateForFileName = (date) => {
	if (!date) return "";
	const d = new Date(date);
	const day = String(d.getDate()).padStart(2, "0");
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const year = d.getFullYear();
	return `${day}-${month}-${year}`;
};

const formatDateDisplay = (date, locale = "pt-BR") => {
	if (!date) return "";
	const d = new Date(date);
	return d.toLocaleDateString(locale, {
		day: "2-digit",
		month: "2-digit",
		year: "numeric"
	});
};

const cleanBusinessName = (name) => {
	if (!name) return "business";
	return name
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]/g, "-")
		.replace(/-+/g, "-")
		.replace(/^-|-$/g, "");
};

const calculateDaysBetween = (fromDate, toDate) => {
	const from = new Date(fromDate);
	const to = new Date(toDate);
	const diffTime = Math.abs(to - from);
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
	return diffDays;
};


export const generateReportPDF = async ({
	business,
	fromDate,
	toDate,
	professionalName,
	isProfessional,
	data,
	currency,
	currencySymbol,
	translations
}) => {
	try {
		const pdf = new jsPDF("p", "mm", "a4");
		const pageWidth = pdf.internal.pageSize.getWidth();
		const pageHeight = pdf.internal.pageSize.getHeight();
		const margin = 20;
		const contentWidth = pageWidth - 2 * margin;
		let yPosition = 0;

		// Calcular estatísticas adicionais
		const totalBookings = data.manualBookings_total + data.onlineBookings_total;
		const daysInPeriod = calculateDaysBetween(fromDate, toDate);
		const averageTicket = totalBookings > 0 ? data.sales_total / totalBookings : 0;
		const clientsPerDay = daysInPeriod > 0 ? (data.clients_total / daysInPeriod).toFixed(1) : 0;
		const onlinePercentage = totalBookings > 0 ? ((data.onlineBookings_total / totalBookings) * 100).toFixed(0) : 0;
		const manualPercentage = totalBookings > 0 ? ((data.manualBookings_total / totalBookings) * 100).toFixed(0) : 0;

		// Header moderno com design profissional
		// Gradiente simulado com múltiplas camadas
		pdf.setFillColor(84, 81, 140);
		pdf.rect(0, 0, pageWidth, 45, "F");
		pdf.setFillColor(71, 68, 120);
		pdf.rect(0, 0, pageWidth, 42, "F");
		
		// Elementos decorativos do header com melhor contraste
		pdf.setFillColor(255, 203, 71);
		pdf.circle(pageWidth - 25, 22, 15, "F");
		pdf.setFillColor(255, 215, 100, 40);
		pdf.circle(pageWidth - 45, 30, 12, "F");
		
		// Logo PRIT com maior destaque
		pdf.setTextColor(255, 255, 255);
		pdf.setFontSize(30);
		pdf.setFont(undefined, "bold");
		pdf.text("PRIT", margin, 22);
		
		// Indicador visual dourado
		pdf.setFillColor(255, 203, 71);
		pdf.circle(margin + 38, 20, 3, "F");
		
		// Subtítulo do relatório
		pdf.setTextColor(255, 255, 255);
		pdf.setFontSize(10);
		pdf.setFont(undefined, "normal");
		pdf.text(translations?.businessReport || "Business Report", margin, 32);
		
		// Nome do business no header
		pdf.setTextColor(255, 255, 255);
		pdf.setFontSize(14);
		pdf.setFont(undefined, "bold");
		const businessText = business?.name || "Business";
		pdf.text(businessText.toUpperCase(), pageWidth - margin, 25, { align: "right" });

		yPosition = 55;

		// Card de informações do período e negócio com design aprimorado
		// Sombra do card
		pdf.setFillColor(240, 240, 245);
		pdf.roundedRect(margin + 1, yPosition + 1, contentWidth, 35, 5, 5, "F");
		
		// Card principal
		pdf.setFillColor(255, 255, 255);
		pdf.setDrawColor(220, 220, 230);
		pdf.setLineWidth(0.5);
		pdf.roundedRect(margin, yPosition, contentWidth, 35, 5, 5, "FD");
		
		// Indicador colorido para período
		pdf.setFillColor(93, 173, 226);
		pdf.circle(margin + 7, yPosition + 8, 2, "F");
		
		// Informações do período
		pdf.setTextColor(100, 100, 110);
		pdf.setFontSize(8);
		pdf.setFont(undefined, "normal");
		pdf.text(translations?.period || "Period", margin + 13, yPosition + 10);
		
		pdf.setTextColor(30, 30, 40);
		pdf.setFontSize(11);
		pdf.setFont(undefined, "bold");
		const periodText = `${formatDateDisplay(fromDate, currencySymbol)} até ${formatDateDisplay(toDate, currencySymbol)}`;
		pdf.text(periodText, margin + 10, yPosition + 20);
		
		// Separador vertical
		pdf.setDrawColor(230, 230, 240);
		pdf.setLineWidth(0.5);
		pdf.line(margin + contentWidth/2, yPosition + 5, margin + contentWidth/2, yPosition + 30);
		
		// Indicador colorido para negócio/profissional
		pdf.setFillColor(155, 89, 182);
		pdf.circle(margin + contentWidth/2 + 7, yPosition + 8, 2, "F");
		
		// Informações do negócio/profissional
		pdf.setTextColor(100, 100, 110);
		pdf.setFontSize(8);
		pdf.setFont(undefined, "normal");
		const labelText = isProfessional ? (translations?.professional || "Professional") : (translations?.business || "Business");
		pdf.text(labelText, margin + contentWidth/2 + 13, yPosition + 10);
		
		pdf.setTextColor(30, 30, 40);
		pdf.setFontSize(11);
		pdf.setFont(undefined, "bold");
		const nameText = isProfessional ? professionalName : businessText;
		pdf.text(nameText, margin + contentWidth/2 + 10, yPosition + 20);
		
		yPosition += 45;

		// KPIs principais com design moderno
		const cardSpacing = 8;
		const cardsPerRow = 3;
		const cardWidth = (contentWidth - (cardsPerRow - 1) * cardSpacing) / cardsPerRow;
		const cardHeight = 38;
		
		// Função para desenhar cards de métricas
		const drawMetricCard = (x, y, title, value, subtitle = "", isMonetary = false, isHighlight = false) => {
			// Sombra do card mais pronunciada
			pdf.setFillColor(230, 230, 235);
			pdf.roundedRect(x + 1.5, y + 1.5, cardWidth, cardHeight, 4, 4, "F");
			
			// Card principal com gradiente simulado
			if (isHighlight) {
				pdf.setFillColor(71, 68, 120);
				pdf.roundedRect(x, y, cardWidth, cardHeight, 4, 4, "F");
				// Overlay sutil para gradiente
				pdf.setFillColor(84, 81, 140, 20);
				pdf.roundedRect(x, y, cardWidth, cardHeight/2, 4, 4, "F");
				// Linha de destaque superior
				pdf.setFillColor(255, 203, 71);
				pdf.rect(x, y, cardWidth, 2, "F");
			} else {
				pdf.setFillColor(255, 255, 255);
				pdf.roundedRect(x, y, cardWidth, cardHeight, 4, 4, "F");
				// Pequena linha colorida no topo
				pdf.setFillColor(230, 230, 240);
				pdf.rect(x, y, cardWidth, 1, "F");
			}
			pdf.setDrawColor(220, 220, 230);
			pdf.setLineWidth(0.3);
			pdf.roundedRect(x, y, cardWidth, cardHeight, 4, 4, "D");
			
			// Título
			pdf.setTextColor(isHighlight ? 220 : 100, isHighlight ? 220 : 100, isHighlight ? 230 : 110);
			pdf.setFontSize(8);
			pdf.setFont(undefined, "normal");
			pdf.text(title, x + cardWidth / 2, y + 10, { align: "center" });
			
			// Valor principal com maior peso
			pdf.setTextColor(isHighlight ? 255 : 30, isHighlight ? 255 : 30, isHighlight ? 255 : 40);
			pdf.setFontSize(isHighlight ? 16 : 15);
			pdf.setFont(undefined, "bold");
			
			let displayValue = value;
			if (isMonetary) {
				displayValue = `${currency}${value.toLocaleString(currencySymbol, {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})}`;
			}
			pdf.text(String(displayValue), x + cardWidth / 2, y + 22, { align: "center" });
			
			// Subtítulo se houver
			if (subtitle) {
				pdf.setTextColor(isHighlight ? 200 : 150, isHighlight ? 200 : 150, isHighlight ? 210 : 160);
				pdf.setFontSize(6);
				pdf.setFont(undefined, "normal");
				pdf.text(subtitle, x + cardWidth / 2, y + 30, { align: "center" });
			}
		};
		
		// Primeira linha de KPIs
		drawMetricCard(
			margin,
			yPosition,
			translations?.sales || "Sales",
			data.sales_total,
			"",
			true,
			true
		);
		
		drawMetricCard(
			margin + cardWidth + cardSpacing,
			yPosition,
			translations?.clients || "Clients",
			data.clients_total,
			"",
			false,
			false
		);
		
		drawMetricCard(
			margin + 2 * (cardWidth + cardSpacing),
			yPosition,
			translations?.totalBookings || "Total Bookings",
			totalBookings,
			"",
			false,
			false
		);
		
		yPosition += cardHeight + 10;
		
		// Segunda linha de KPIs
		drawMetricCard(
			margin,
			yPosition,
			translations?.averageTicket || "Average Ticket",
			averageTicket,
			"",
			true,
			false
		);
		
		drawMetricCard(
			margin + cardWidth + cardSpacing,
			yPosition,
			translations?.clientsPerDay || "Clients per Day",
			clientsPerDay,
			"",
			false,
			false
		);
		
		// Card especial de distribuição percentual
		const percX = margin + 2 * (cardWidth + cardSpacing);
		const percY = yPosition;
		
		// Sombra mais pronunciada
		pdf.setFillColor(230, 230, 235);
		pdf.roundedRect(percX + 1.5, percY + 1.5, cardWidth, cardHeight, 4, 4, "F");
		
		// Card
		pdf.setFillColor(255, 255, 255);
		pdf.setDrawColor(220, 220, 230);
		pdf.setLineWidth(0.3);
		pdf.roundedRect(percX, percY, cardWidth, cardHeight, 4, 4, "FD");
		
		// Título com indicador visual
		pdf.setFillColor(100, 180, 220);
		pdf.rect(percX, percY, cardWidth, 1, "F");
		
		pdf.setTextColor(100, 100, 110);
		pdf.setFontSize(8);
		pdf.setFont(undefined, "normal");
		pdf.text(`${translations?.distribution || "Distribution"} %`, percX + cardWidth / 2, percY + 10, { align: "center" });
		
		// Valores percentuais lado a lado
		const percentWidth = cardWidth / 2 - 5;
		
		// Online
		pdf.setFillColor(93, 173, 226);
		pdf.rect(percX + 5, percY + 15, percentWidth - 2, 12, "F");
		pdf.setTextColor(255, 255, 255);
		pdf.setFontSize(9);
		pdf.setFont(undefined, "bold");
		pdf.text(`${translations?.online || "Online"}: ${onlinePercentage}%`, percX + 5 + percentWidth/2, percY + 22, { align: "center" });
		
		// Manual
		pdf.setFillColor(155, 89, 182);
		pdf.rect(percX + cardWidth/2 + 2, percY + 15, percentWidth - 2, 12, "F");
		pdf.setTextColor(255, 255, 255);
		pdf.text(`${translations?.manual || "Manual"}: ${manualPercentage}%`, percX + cardWidth/2 + 2 + percentWidth/2, percY + 22, { align: "center" });
		
		yPosition += cardHeight + 15;

		// Seção de detalhamento de agendamentos com sombra
		pdf.setFillColor(248, 248, 250);
		pdf.roundedRect(margin + 1, yPosition + 1, contentWidth, 30, 5, 5, "F");
		
		pdf.setFillColor(255, 255, 255);
		pdf.setDrawColor(230, 230, 240);
		pdf.setLineWidth(0.5);
		pdf.roundedRect(margin, yPosition, contentWidth, 30, 5, 5, "FD");
		
		// Indicador lateral colorido com gradiente
		pdf.setFillColor(255, 203, 71);
		pdf.rect(margin, yPosition, 4, 30, "F");
		pdf.setFillColor(255, 215, 100, 30);
		pdf.rect(margin, yPosition, 4, 15, "F");
		
		// Título da seção
		pdf.setTextColor(40, 40, 50);
		pdf.setFontSize(10);
		pdf.setFont(undefined, "bold");
		pdf.text(translations?.bookings || "Bookings", margin + 10, yPosition + 10);
		
		// Valores detalhados
		pdf.setFontSize(8);
		pdf.setFont(undefined, "normal");
		
		// Online
		pdf.setTextColor(93, 173, 226);
		pdf.text(`${translations?.onlineBookings || "Online Bookings"}: ${data.onlineBookings_total}`, margin + 10, yPosition + 20);
		
		// Manual
		pdf.setTextColor(155, 89, 182);
		pdf.text(`${translations?.manualBookings || "Manual Bookings"}: ${data.manualBookings_total}`, margin + contentWidth/2, yPosition + 20);
		
		yPosition += 40;

		// Seção de gráficos
		if (yPosition > pageHeight - 90) {
			pdf.addPage();
			yPosition = margin;
		}
		
		// Título da seção de gráficos
		pdf.setTextColor(40, 40, 50);
		pdf.setFontSize(12);
		pdf.setFont(undefined, "bold");
		pdf.text(translations?.sales || "Sales", margin, yPosition);
		yPosition += 8;
		
		// Capturar gráfico de receita
		const salesChart = document.getElementById("sales-chart");
		if (salesChart && salesChart.querySelector("svg")) {
			try {
				const canvas = await html2canvas(salesChart, {
					scale: 3,
					backgroundColor: "#ffffff",
					logging: false,
					useCORS: true,
				});
				
				const imgData = canvas.toDataURL("image/png");
				const imgWidth = contentWidth;
				const imgHeight = (canvas.height * imgWidth) / canvas.width * 0.5;
				
				// Container do gráfico
				pdf.setFillColor(255, 255, 255);
				pdf.setDrawColor(230, 230, 240);
				pdf.setLineWidth(0.5);
				pdf.roundedRect(margin, yPosition, imgWidth, imgHeight + 10, 5, 5, "FD");
				
				pdf.addImage(imgData, "PNG", margin + 5, yPosition + 5, imgWidth - 10, imgHeight);
				yPosition += imgHeight + 20;
			} catch (error) {
				yPosition += 0;
			}
		}
		
		// Verificar se precisa nova página para próximos gráficos
		if (yPosition > pageHeight - 90) {
			pdf.addPage();
			yPosition = margin;
		}
		
		// Adicionar página 2 com os outros gráficos
		pdf.addPage();
		yPosition = margin;
		
		// Título da página 2
		pdf.setTextColor(40, 40, 50);
		pdf.setFontSize(12);
		pdf.setFont(undefined, "bold");
		pdf.text(translations?.bookings || "Bookings", margin, yPosition);
		yPosition += 8;
		
		// Gráfico de agendamentos
		const numbersChart = document.getElementById("numbers-chart");
		if (numbersChart && numbersChart.querySelector("svg")) {
			try {
				const canvas = await html2canvas(numbersChart, {
					scale: 3,
					backgroundColor: "#ffffff",
					logging: false,
					useCORS: true,
				});
				
				const imgData = canvas.toDataURL("image/png");
				const imgWidth = contentWidth;
				const imgHeight = (canvas.height * imgWidth) / canvas.width * 0.5;
				
				// Container do gráfico
				pdf.setFillColor(255, 255, 255);
				pdf.setDrawColor(230, 230, 240);
				pdf.setLineWidth(0.5);
				pdf.roundedRect(margin, yPosition, imgWidth, imgHeight + 10, 5, 5, "FD");
				
				pdf.addImage(imgData, "PNG", margin + 5, yPosition + 5, imgWidth - 10, imgHeight);
				yPosition += imgHeight + 20;
			} catch (error) {
				console.error("Erro ao capturar gráfico de agendamentos:", error);
			}
		}
		
		// Título para gráfico de clientes
		pdf.setTextColor(40, 40, 50);
		pdf.setFontSize(12);
		pdf.setFont(undefined, "bold");
		pdf.text(translations?.clients || "Clients", margin, yPosition);
		yPosition += 8;
		
		// Gráfico de clientes
		const clientsChart = document.getElementById("clients-chart");
		if (clientsChart && clientsChart.querySelector("svg")) {
			try {
				const canvas = await html2canvas(clientsChart, {
					scale: 3,
					backgroundColor: "#ffffff",
					logging: false,
					useCORS: true,
				});
				
				const imgData = canvas.toDataURL("image/png");
				const imgWidth = contentWidth;
				const imgHeight = (canvas.height * imgWidth) / canvas.width * 0.5;
				
				// Container do gráfico
				pdf.setFillColor(255, 255, 255);
				pdf.setDrawColor(230, 230, 240);
				pdf.setLineWidth(0.5);
				pdf.roundedRect(margin, yPosition, imgWidth, imgHeight + 10, 5, 5, "FD");
				
				pdf.addImage(imgData, "PNG", margin + 5, yPosition + 5, imgWidth - 10, imgHeight);
				yPosition += imgHeight + 20;
			} catch (error) {
				console.error("Erro ao capturar gráfico de clientes:", error);
			}
		}

		// Footer em todas as páginas
		const totalPages = pdf.internal.getNumberOfPages();
		for (let page = 1; page <= totalPages; page++) {
			pdf.setPage(page);
			
			const footerY = pageHeight - 20;
			
			// Linha divisória
			pdf.setDrawColor(230, 230, 240);
			pdf.setLineWidth(0.5);
			pdf.line(margin, footerY, pageWidth - margin, footerY);
			
			// Logo PRIT
			pdf.setTextColor(71, 68, 120);
			pdf.setFontSize(8);
			pdf.setFont(undefined, "bold");
			pdf.text("PRIT", margin, footerY + 10);
			
			// Data de geração
			pdf.setTextColor(120, 120, 130);
			pdf.setFontSize(7);
			pdf.setFont(undefined, "normal");
			
			const currentDate = new Date().toLocaleDateString("pt-BR");
			const currentTime = new Date().toLocaleTimeString("pt-BR", { 
				hour: "2-digit", 
				minute: "2-digit" 
			});
			
			pdf.text(
				`${translations?.generatedAt || "Generated at"}: ${currentDate} ${currentTime}`,
				pageWidth / 2,
				footerY + 10,
				{ align: "center" }
			);
			
			// Número da página
			pdf.setTextColor(71, 68, 120);
			pdf.text(
				`${page} / ${totalPages}`,
				pageWidth - margin,
				footerY + 10,
				{ align: "right" }
			);
		}

		// Gerar nome do arquivo
		const currentLanguage = i18n.language || 'pt-BR';
		const reportWord = currentLanguage.includes('en') ? 'report' :
			currentLanguage.includes('es') ? 'informe' : 'relatorio';
		const reportType = isProfessional ?
			(currentLanguage.includes('en') ? 'professional' :
				currentLanguage.includes('es') ? 'profesional' : 'profissional') :
			(currentLanguage.includes('en') ? 'business' :
				currentLanguage.includes('es') ? 'negocio' : 'negocio');
		const businessNameForFile = isProfessional ? professionalName : (business?.name || translations?.business || "business");
		const fileName = `${reportWord}-prit-${reportType}-${cleanBusinessName(
			businessNameForFile
		)}-${formatDateForFileName(fromDate)}-${formatDateForFileName(toDate)}.pdf`;

		if (isWebView()) {
			if (isIOSWebView()) {
				const viewerResult = openPDFSimple(pdf, fileName);
				return {
					...viewerResult,
					fileName,
					isWebView: true,
					platform: 'iOS'
				};
			}

			const viewerResult = openPDFSimple(pdf, fileName);
			return {
				...viewerResult,
				fileName,
				isWebView: true,
				platform: 'Android/Other'
			};
		} else {
			pdf.save(fileName);

			return {
				success: true,
				fileName,
				isWebView: false,
				platform: 'Browser'
			};
		}
	} catch (error) {
		return { success: false, error: error.message };
	}
};